import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';
import '../styles/pages/_quiz-completion.scss';
import '../styles/pages/_quiz.scss';

const quizQuestions = [
  {
    question: "What is the best way to integrate environmental well-being into daily life?",
    options: [
      "Small, consistent environmental practices",
      "Ignoring environmental concerns",
      "Only weekend activities"
    ],
    correctAnswer: 0,
    explanation: "Integrating environmental well-being into daily life involves small, consistent environmental practices that become habits, creating sustainable positive changes over time."
  },
  {
    question: "How does environmental well-being connect to other aspects of holistic wellness?",
    options: [
      "It has no connection to other wellness areas",
      "It influences physical, mental, and social well-being through air quality, stress reduction, and community connections",
      "It only affects physical health"
    ],
    correctAnswer: 1,
    explanation: "Environmental well-being is interconnected with other wellness domains - clean environments support physical health, restorative spaces reduce stress for mental well-being, and sustainable communities foster social connections."
  },
  {
    question: "What is environmental mindfulness?",
    options: [
      "Being aware of environmental issues only when convenient",
      "Conscious awareness of how our surroundings and choices affect our well-being and the planet",
      "Ignoring the impact of our environment"
    ],
    correctAnswer: 1,
    explanation: "Environmental mindfulness involves being consciously aware of how our physical surroundings, daily choices, and environmental conditions affect our personal well-being and the broader planetary ecosystem."
  },
  {
    question: "What is meant by the 'ripple effect' of environmental choices?",
    options: [
      "Environmental actions have no lasting impact",
      "One person's sustainable choices can inspire others and create broader positive change",
      "Only large-scale corporate actions matter"
    ],
    correctAnswer: 1,
    explanation: "The ripple effect refers to how individual environmental choices can inspire family, friends, and community members to adopt similar practices, creating waves of positive environmental change."
  },
  {
    question: "What is the most effective way to maintain environmental habits long-term?",
    options: [
      "Relying only on motivation",
      "Creating supportive environments and systems that make sustainable choices easier",
      "Expecting perfection and giving up when mistakes happen"
    ],
    correctAnswer: 1,
    explanation: "Long-term environmental habits are best maintained by creating supportive systems and environments that make sustainable choices the default option, rather than relying solely on willpower or motivation."
  }
];

const EnvironmentalModule6QuizPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress } = useModuleProgress('environmentalM6');
  const [currentQuestion, setCurrentQuestion] = useState<number>(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [responses, setResponses] = useState<{[key: number]: number}>({});
  const [showCompletion, setShowCompletion] = useState(false);

  const handleAnswerSelect = (answerIndex: number) => {
    if (responses[currentQuestion] === undefined) {
      setSelectedAnswer(answerIndex);
      setShowExplanation(true);
      setResponses(prev => ({...prev, [currentQuestion]: answerIndex}));
    }
  };

  const handleNext = () => {
    if (currentQuestion < quizQuestions.length - 1) {
      const nextQuestion = currentQuestion + 1;
      setCurrentQuestion(nextQuestion);
      setSelectedAnswer(null);
      setShowExplanation(false);
      saveProgress(nextQuestion + 1);
    } else {
      saveProgress(quizQuestions.length, true);
      setShowCompletion(true);
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      const prevQuestion = currentQuestion - 1;
      setCurrentQuestion(prevQuestion);
      setSelectedAnswer(responses[prevQuestion] || null);
      setShowExplanation(responses[prevQuestion] !== undefined);
    }
  };

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading quiz...</p>
      </div>
    );
  }

  if (showCompletion) {
    return (
      <div className="quiz-completion-section">
        <h2 className="completion-title">
          Congratulations!
        </h2>
        <p className="completion-subtitle">
          You've successfully completed the Module 6 quiz!
        </p>
        <div className="completion-card">
          <div className="achievement-section">
            <h3 className="section-title">
              Key Achievements
            </h3>
            <p className="achievement-intro">You've demonstrated a great understanding of:</p>
            <ul className="achievement-list">
              <li>
                Holistic environmental integration
              </li>
              <li>
                Environmental mindfulness
              </li>
              <li>
                The ripple effect of environmental choices
              </li>
            </ul>
          </div>
          <div className="next-steps-section">
            <h3 className="section-title">
              Next Steps
            </h3>
            <p>Congratulations on completing all environmental wellness modules!</p>
            <p>You've gained comprehensive knowledge about environmental well-being and its integration into holistic health. Continue applying these principles in your daily life.</p>
          </div>
          <button
            className="resource-button"
            onClick={() => navigate('/panel/learn/environmental-resources')}
          >
            Return to Environmental Resources
          </button>
        </div>
      </div>
    );
  }

  const progress = ((currentQuestion + 1) / quizQuestions.length) * 100;
  const currentQuiz = quizQuestions[currentQuestion];

  return (
    <div className="module-form">
      <div className="module-container">
        <div className="module-left environmental-panel">
          <div className="module-header">
            <h1>Module 6 Quiz</h1>
            <p className="module-subtitle">Integrating Environmental Well-Being into Holistic Life Balance</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Let's review what we've learned about integrating environmental well-being into holistic health.
              There's no scoring - this is just to reinforce the main ideas!
            </p>
          </div>
        </div>
        <div className="assessment-right-panel">
          <div className="assessment-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Question {currentQuestion + 1} of {quizQuestions.length}
              </span>
            </div>
            <div className="question-section">
              <h2 className="question-title">
                {currentQuiz.question}
              </h2>
              <p className="question-instruction">
                Please select the answer that best matches your understanding.
              </p>
              <div className="answer-options">
                {currentQuiz.options.map((option, index) => (
                  <label key={index} className="answer-option">
                    <input
                      type="radio"
                      name={`question-${currentQuestion}`}
                      value={index}
                      checked={selectedAnswer === index}
                      onChange={() => handleAnswerSelect(index)}
                      disabled={selectedAnswer !== null}
                    />
                    <span className="option-text">{option}</span>
                  </label>
                ))}
              </div>
              {showExplanation && (
                <div className="explanation">
                  <h3>{selectedAnswer === currentQuiz.correctAnswer ? 'Correct!' : 'Remember:'}</h3>
                  <p>{currentQuiz.explanation}</p>
                </div>
              )}
            </div>
            <div className="navigation-buttons">
              <button
                className="btn-secondary"
                onClick={currentQuestion === 0 ? () => navigate('/panel/learn/environmental-resources') : handleBack}
              >
                {currentQuestion === 0 ? 'Exit Quiz' : 'Previous Question'}
              </button>
              {currentQuestion < quizQuestions.length - 1 ? (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={selectedAnswer === null}
                >
                  Next Question
                </button>
              ) : (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={selectedAnswer === null}
                >
                  Complete Quiz
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnvironmentalModule6QuizPage;

